// ===========================================
// Permission Slip Hooks
// ===========================================
// Admin, parent and public (magic-link) hooks in one file — they share a query
// key namespace, and splitting them would make the invalidation relationships
// harder to see rather than easier.

import { useMutation, useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { dashboardKeys } from './useDashboard';
import {
  permissionSlipApi,
  permissionSlipParentApi,
  permissionSlipPublicApi,
  PermissionSlip,
  PermissionSlipInput,
  PermissionSlipResponse,
  PermissionSlipStatus,
  SignSlipInput,
  SlipEmailRequest,
  SlipResponseStats,
  StudentMedicalInfo,
} from '../api/permissionSlips.api';

type ApiError = Error & { response?: { data?: { message?: string } } };
const errMsg = (e: unknown, fallback: string) =>
  (e as ApiError)?.response?.data?.message || fallback;

// ===========================================
// QUERY KEYS
// ===========================================

export const permissionSlipKeys = {
  all: ['permissionSlips'] as const,
  lists: () => [...permissionSlipKeys.all, 'list'] as const,
  list: (filters?: { status?: PermissionSlipStatus; eventId?: string }) =>
    [...permissionSlipKeys.lists(), filters] as const,
  detail: (id: string) => [...permissionSlipKeys.all, 'detail', id] as const,
  stats: (id: string) => [...permissionSlipKeys.all, 'stats', id] as const,
  responses: (id: string) => [...permissionSlipKeys.all, 'responses', id] as const,
};

export const parentSlipKeys = {
  all: ['parentPermissionSlips'] as const,
  list: () => [...parentSlipKeys.all, 'list'] as const,
  pendingCount: () => [...parentSlipKeys.all, 'pendingCount'] as const,
  detail: (id: string) => [...parentSlipKeys.all, 'detail', id] as const,
  medical: (studentId: string) => [...parentSlipKeys.all, 'medical', studentId] as const,
  publicToken: (token: string) => [...parentSlipKeys.all, 'public', token] as const,
};

// ===========================================
// ADMIN
// ===========================================

export function usePermissionSlips(filters?: {
  status?: PermissionSlipStatus;
  eventId?: string;
}): UseQueryResult<PermissionSlip[], Error> {
  return useQuery({
    queryKey: permissionSlipKeys.list(filters),
    queryFn: () => permissionSlipApi.list(filters),
  });
}

export function usePermissionSlip(id: string): UseQueryResult<PermissionSlip, Error> {
  return useQuery({
    queryKey: permissionSlipKeys.detail(id),
    queryFn: () => permissionSlipApi.get(id),
    enabled: !!id,
  });
}

export function useSlipStats(id: string): UseQueryResult<SlipResponseStats, Error> {
  return useQuery({
    queryKey: permissionSlipKeys.stats(id),
    queryFn: () => permissionSlipApi.stats(id),
    enabled: !!id,
    // Parents sign from their own devices at any time, so a cached count goes
    // stale the moment an admin looks away.
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });
}

export function useSlipResponses(id: string): UseQueryResult<PermissionSlipResponse[], Error> {
  return useQuery({
    queryKey: permissionSlipKeys.responses(id),
    queryFn: () => permissionSlipApi.responses(id),
    enabled: !!id,
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
}

/**
 * Render the slip's email for the editor's Preview (#53). A mutation, not a
 * query: it is driven by the editor's UNSAVED values, sent in the request body,
 * and is only fetched when the admin opens or changes the preview.
 */
export function useSlipEmailPreview(id: string) {
  return useMutation({
    mutationFn: (input: SlipEmailRequest) => permissionSlipApi.emailPreview(id, input),
  });
}

/** "Send test to me": the email goes to the signed-in admin's own address only. */
export function useSendSlipTestEmail(id: string) {
  const { enqueueSnackbar } = useSnackbar();
  return useMutation({
    mutationFn: (input: SlipEmailRequest) => permissionSlipApi.sendTestEmail(id, input),
    onSuccess: (data) =>
      enqueueSnackbar(`Test email sent to ${data.to}`, { variant: 'success' }),
    onError: (e) => enqueueSnackbar(errMsg(e, 'Failed to send the test email'), { variant: 'error' }),
  });
}

export function useCreatePermissionSlip() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation({
    mutationFn: (data: PermissionSlipInput) => permissionSlipApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: permissionSlipKeys.lists() });
      enqueueSnackbar('Permission slip created', { variant: 'success' });
    },
    onError: (e) => enqueueSnackbar(errMsg(e, 'Failed to create permission slip'), { variant: 'error' }),
  });
}

export function useUpdatePermissionSlip() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<PermissionSlipInput> }) =>
      permissionSlipApi.update(id, data),
    onSuccess: (_: PermissionSlip, { id }) => {
      queryClient.invalidateQueries({ queryKey: permissionSlipKeys.lists() });
      queryClient.invalidateQueries({ queryKey: permissionSlipKeys.detail(id) });
      enqueueSnackbar('Permission slip updated', { variant: 'success' });
    },
    onError: (e) => enqueueSnackbar(errMsg(e, 'Failed to update permission slip'), { variant: 'error' }),
  });
}

export function useDeletePermissionSlip() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation({
    mutationFn: (id: string) => permissionSlipApi.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: permissionSlipKeys.lists() });
      enqueueSnackbar('Permission slip deleted', { variant: 'success' });
    },
    // A sent slip, or one with responses, is refused server-side (409) — those
    // are consent records. Surface the server's wording rather than a generic
    // failure, because it explains WHY.
    onError: (e) => enqueueSnackbar(errMsg(e, 'Failed to delete permission slip'), { variant: 'error' }),
  });
}

export function useSendPermissionSlip() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation({
    mutationFn: (id: string) => permissionSlipApi.send(id),
    onSuccess: (result, id) => {
      queryClient.invalidateQueries({ queryKey: permissionSlipKeys.lists() });
      queryClient.invalidateQueries({ queryKey: permissionSlipKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: permissionSlipKeys.stats(id) });
      // `claimed: false` means the compare-and-set lost — the cron sweep or a
      // second click already sent it. Not an error, but not a fresh send either.
      enqueueSnackbar(
        result.claimed ? 'Permission slip sent' : 'This permission slip was already sent',
        { variant: result.claimed ? 'success' : 'info' }
      );
    },
    onError: (e) => enqueueSnackbar(errMsg(e, 'Failed to send permission slip'), { variant: 'error' }),
  });
}

export function useSchedulePermissionSlip() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation({
    mutationFn: ({ id, scheduledFor }: { id: string; scheduledFor: string }) =>
      permissionSlipApi.schedule(id, scheduledFor),
    onSuccess: (_: PermissionSlip, { id }) => {
      queryClient.invalidateQueries({ queryKey: permissionSlipKeys.lists() });
      queryClient.invalidateQueries({ queryKey: permissionSlipKeys.detail(id) });
      enqueueSnackbar('Permission slip scheduled', { variant: 'success' });
    },
    onError: (e) => enqueueSnackbar(errMsg(e, 'Failed to schedule permission slip'), { variant: 'error' }),
  });
}

export function useRemindPermissionSlip() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation({
    mutationFn: (id: string) => permissionSlipApi.remind(id),
    onSuccess: (result, id) => {
      queryClient.invalidateQueries({ queryKey: permissionSlipKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: permissionSlipKeys.stats(id) });
      enqueueSnackbar(
        result.families === 0
          ? 'Everyone has already responded — no reminders sent'
          : `Reminder sent to ${result.families} ${result.families === 1 ? 'family' : 'families'}`,
        { variant: result.families === 0 ? 'info' : 'success' }
      );
    },
    onError: (e) => enqueueSnackbar(errMsg(e, 'Failed to send reminder'), { variant: 'error' }),
  });
}

export function useRefreshSlipAudience() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation({
    mutationFn: (id: string) => permissionSlipApi.refreshAudience(id),
    onSuccess: (result, id) => {
      queryClient.invalidateQueries({ queryKey: permissionSlipKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: permissionSlipKeys.stats(id) });
      queryClient.invalidateQueries({ queryKey: permissionSlipKeys.responses(id) });
      enqueueSnackbar(
        result.added === 0
          ? 'No new students match this permission slip'
          : `Added ${result.added} ${result.added === 1 ? 'student' : 'students'} and sent to ${result.families} ${result.families === 1 ? 'family' : 'families'}`,
        { variant: result.added === 0 ? 'info' : 'success' }
      );
    },
    onError: (e) => enqueueSnackbar(errMsg(e, 'Failed to refresh the audience'), { variant: 'error' }),
  });
}

export function useClosePermissionSlip() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation({
    mutationFn: (id: string) => permissionSlipApi.close(id),
    onSuccess: (_: PermissionSlip, id) => {
      queryClient.invalidateQueries({ queryKey: permissionSlipKeys.lists() });
      queryClient.invalidateQueries({ queryKey: permissionSlipKeys.detail(id) });
      enqueueSnackbar('Permission slip closed — outstanding links revoked', { variant: 'success' });
    },
    onError: (e) => enqueueSnackbar(errMsg(e, 'Failed to close permission slip'), { variant: 'error' }),
  });
}

// ===========================================
// PARENT
// ===========================================

export function useParentPermissionSlips(options?: {
  enabled?: boolean;
}): UseQueryResult<PermissionSlipResponse[], Error> {
  return useQuery({
    queryKey: parentSlipKeys.list(),
    queryFn: () => permissionSlipParentApi.list(),
    enabled: options?.enabled ?? true,
  });
}

/**
 * Pending count for the nav badge.
 *
 * Derived from the list the portal page already loads, mirroring
 * `useParentUnpaidInvoiceCount` — one query serves both surfaces rather than a
 * second endpoint for a number.
 */
export function usePendingPermissionSlipCount(options?: { enabled?: boolean }) {
  const { data: responses = [], isLoading } = useParentPermissionSlips(options);
  const count = responses.filter(
    (r) => r.status === 'PENDING' && r.PermissionSlip?.status === 'SENT'
  ).length;
  return { count, isLoading };
}

export function useParentPermissionSlip(slipId: string) {
  return useQuery({
    queryKey: parentSlipKeys.detail(slipId),
    queryFn: () => permissionSlipParentApi.get(slipId),
    enabled: !!slipId,
    staleTime: 0,
  });
}

export function useSignPermissionSlip() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation({
    mutationFn: ({ slipId, data }: { slipId: string; data: SignSlipInput }) =>
      permissionSlipParentApi.sign(slipId, data),
    onSuccess: (_: PermissionSlipResponse, { slipId }) => {
      queryClient.invalidateQueries({ queryKey: parentSlipKeys.list() });
      queryClient.invalidateQueries({ queryKey: parentSlipKeys.pendingCount() });
      queryClient.invalidateQueries({ queryKey: parentSlipKeys.detail(slipId) });
      // The dashboard notification feed is a separate query — bust it too, or
      // a signed slip keeps nagging from the dashboard. (Using the real key
      // factory: a hand-written literal here would silently match nothing.)
      queryClient.invalidateQueries({ queryKey: dashboardKeys.parent.notifications() });
      enqueueSnackbar('Permission slip signed', { variant: 'success' });
    },
    onError: (e) => enqueueSnackbar(errMsg(e, 'Failed to sign permission slip'), { variant: 'error' }),
  });
}

export function useDeclinePermissionSlip() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation({
    mutationFn: ({
      slipId,
      studentId,
      parentNotes,
    }: {
      slipId: string;
      studentId: string;
      parentNotes?: string;
    }) => permissionSlipParentApi.decline(slipId, studentId, parentNotes),
    onSuccess: (_: PermissionSlipResponse, { slipId }) => {
      queryClient.invalidateQueries({ queryKey: parentSlipKeys.list() });
      queryClient.invalidateQueries({ queryKey: parentSlipKeys.pendingCount() });
      queryClient.invalidateQueries({ queryKey: parentSlipKeys.detail(slipId) });
      queryClient.invalidateQueries({ queryKey: dashboardKeys.parent.notifications() });
      enqueueSnackbar('Response recorded', { variant: 'success' });
    },
    onError: (e) => enqueueSnackbar(errMsg(e, 'Failed to record response'), { variant: 'error' }),
  });
}

export function useStudentMedicalInfo(studentId: string, enabled = true) {
  return useQuery({
    queryKey: parentSlipKeys.medical(studentId),
    queryFn: () => permissionSlipParentApi.getMedical(studentId),
    enabled: !!studentId && enabled,
  });
}

export function useUpdateStudentMedicalInfo() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation({
    mutationFn: ({ studentId, data }: { studentId: string; data: Partial<StudentMedicalInfo> }) =>
      permissionSlipParentApi.updateMedical(studentId, data),
    onSuccess: (_: StudentMedicalInfo, { studentId }) => {
      queryClient.invalidateQueries({ queryKey: parentSlipKeys.medical(studentId) });
      enqueueSnackbar('Medical details saved', { variant: 'success' });
    },
    onError: (e) => enqueueSnackbar(errMsg(e, 'Failed to save medical details'), { variant: 'error' }),
  });
}

// ===========================================
// PUBLIC (magic link)
// ===========================================

export function usePublicPermissionSlip(token: string) {
  return useQuery({
    queryKey: parentSlipKeys.publicToken(token),
    queryFn: () => permissionSlipPublicApi.get(token),
    enabled: !!token,
    // A bad or expired token is a permanent 404, not a transient failure —
    // retrying just burns the token-keyed rate limit budget.
    retry: false,
    staleTime: 0,
  });
}

export function useSignPublicPermissionSlip() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation({
    mutationFn: ({ token, data }: { token: string; data: SignSlipInput }) =>
      permissionSlipPublicApi.sign(token, data),
    onSuccess: (_: PermissionSlipResponse, { token }) => {
      queryClient.invalidateQueries({ queryKey: parentSlipKeys.publicToken(token) });
      enqueueSnackbar('Thank you — your response has been recorded', { variant: 'success' });
    },
    onError: (e) => enqueueSnackbar(errMsg(e, 'Failed to record your response'), { variant: 'error' }),
  });
}

export function useDeclinePublicPermissionSlip() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation({
    mutationFn: ({
      token,
      studentId,
      parentNotes,
    }: {
      token: string;
      studentId: string;
      parentNotes?: string;
    }) => permissionSlipPublicApi.decline(token, studentId, parentNotes),
    onSuccess: (_: PermissionSlipResponse, { token }) => {
      queryClient.invalidateQueries({ queryKey: parentSlipKeys.publicToken(token) });
      enqueueSnackbar('Thank you — your response has been recorded', { variant: 'success' });
    },
    onError: (e) => enqueueSnackbar(errMsg(e, 'Failed to record your response'), { variant: 'error' }),
  });
}
